import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { cn } from '@/shared/helpers/cn';
import styles from './Switch.module.scss';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
}

/**
 * Chave liga/desliga.
 *
 * Para a opcao que **muda o comportamento do sistema na hora** — "permitir
 * agendamento fora do horario", por exemplo —, e nao para escolher itens de uma
 * lista: isso e caixa de selecao.
 *
 * Por dentro e um `input[type=checkbox]` com `role="switch"`: o controle nativo
 * traz teclado, foco e formulario de graça, e o papel e o que faz o leitor de
 * tela anunciar "ativado/desativado" em vez de "marcado".
 *
 * O estado **nao** e comunicado so pela cor: a chave desliza de um lado ao outro,
 * e a posicao do botao e o sinal que sobrevive a monitor ruim e a daltonismo.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, hint, className, id: providedId, disabled, ...rest },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const hintId = `${id}-hint`;

  return (
    <div className={cn(styles.root, disabled && styles.desabilitado, className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        role="switch"
        className={styles.input}
        disabled={disabled}
        aria-describedby={hint ? hintId : undefined}
        {...rest}
      />
      <label className={styles.label} htmlFor={id}>
        <span className={styles.trilho} aria-hidden="true">
          <span className={styles.botao} />
        </span>
        <span className={styles.textos}>
          <span className={styles.titulo}>{label}</span>
          {hint ? (
            <span className={styles.hint} id={hintId}>
              {hint}
            </span>
          ) : null}
        </span>
      </label>
    </div>
  );
});
